import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'

import {useConnectors} from '../hooks/use-connectors'
import {useMcpServers} from '../hooks/use-mcp-servers'
import {CustomizeEmptyState} from './CustomizeEmptyState'
import {CustomizePage} from './CustomizePage'

/**
 * Customize UI without any route data dependency, so it can render both as a
 * standalone page and as a route inside the Copilot immersive app.
 *
 * The boundary is scoped to this view rather than left to the route, so a crash
 * here keeps the surrounding Copilot shell and its navigation usable.
 */
export function CustomizeView() {
  return (
    <ErrorBoundary
      boundaryName="copilot-connectors"
      fallback={
        <CustomizeEmptyState
          headingLevel="h2"
          heading="Customize could not be displayed"
          description="Something went wrong while rendering your customizations."
          onRetry={() => window.location.reload()}
          retryLabel="Reload"
        />
      }
    >
      <CustomizeViewContent />
    </ErrorBoundary>
  )
}

function CustomizeViewContent() {
  const {plugins, isLoading, loadError, actionError, busyConnectors, connect, disconnect, refresh} = useConnectors()
  const mcpFlagEnabled = isFeatureEnabled('one_copilot_personal_mcp_config')
  const mcp = useMcpServers(mcpFlagEnabled)
  const mcpEnabled = mcp.available

  return (
    <CustomizePage
      plugins={plugins}
      loading={isLoading}
      loadError={loadError}
      actionError={actionError}
      busyConnectors={busyConnectors}
      onRetry={refresh}
      onConnect={connect}
      onDisconnect={disconnect}
      mcpServers={mcpEnabled ? mcp.servers : undefined}
      mcpLoading={mcp.isLoading}
      mcpLoadError={mcp.loadError}
      mcpLoadErrorAt={mcp.loadErrorAt}
      onRetryMcp={mcp.refresh}
      onSaveMcpServer={mcpEnabled ? mcp.save : undefined}
      onDeleteMcpServer={mcpEnabled ? mcp.remove : undefined}
    />
  )
}
